import { getInitials, ListItem } from '@features/common';
import { ClienteSuite } from '@interfaces/firestore-documents.interfaces';
import { AdminUserData } from '@interfaces/requests.interfaces';
import {
	Avatar,
	Chip,
	Grid,
	Switch,
	Theme,
	Typography,
	useMediaQuery,
	Tooltip,
} from '@mui/material';
import { stringToColor } from '@utils/colors';
import useCustomerInformationStyles from './CustomerInformation.styles';
import useRequestsTableStyles from '../RequestsTable/RequestsTable.styles';
import useRequests from '@hooks/requests.hook';
import React, { useEffect, useState, useRef } from 'react';

interface Props {
	data: ClienteSuite;
}

interface TooltipTextProps {
	text: string;
	id: string;
	title: string;
	margin?: boolean;
	style?: React.CSSProperties;
}

const TooltipText: React.FC<TooltipTextProps> = ({ text, id, title, margin, style }) => {
	const [isOverflowing, setIsOverflowing] = useState(false);
	const textElementRef = useRef<HTMLSpanElement>(null);
	const tooltipClasses = useRequestsTableStyles();

	useEffect(() => {
		const checkOverflow = () => {
			if (textElementRef.current) {
				const isOverflow =
					textElementRef.current.scrollWidth > textElementRef.current.clientWidth;
				setIsOverflowing(isOverflow);
			}
		};

		checkOverflow();
		window.addEventListener('resize', checkOverflow);

		return () => {
			window.removeEventListener('resize', checkOverflow);
		};
	}, [text]);

	return (
		<div
			className={margin ? 'margin' : ''}
			id={id}
			style={{
				...style,
				...(margin && { marginTop: 12 }),
			}}
		>
			<span
				style={{
					color: 'rgba(0, 0, 0, 0.6)',
					display: 'block',
					fontFamily: 'Montserrat',
					fontSize: 14,
				}}
			>
				{title}
			</span>
			<Tooltip
				title={isOverflowing ? text : ''}
				placement="bottom"
				arrow
				classes={{
					tooltip: tooltipClasses.tooltipBottomCampo,
					arrow: tooltipClasses.arrowBottom,
				}}
			>
				<span
					ref={textElementRef}
					id={`${id}-value`}
					style={{
						color: '#1A1233',
						display: 'block',
						fontFamily: 'Montserrat',
						fontSize: 12,
						fontWeight: 600,
						maxWidth: '100%',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{text}
				</span>
			</Tooltip>
		</div>
	);
};

const CustomerInformation: React.FC<Props> = ({ data }) => {
	// Hooks
	const classes = useCustomerInformationStyles();
	const { requestAdministraCliente, requestBackendSuite } = useRequests();
	const [valueHabilitar, setValueHabilitar] = useState(false);
	const [adminUser, setAdminUser] = useState<AdminUserData | null>(null);
	// Breakpoints
	const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

	// Variables
	const houseNumber =
		data.telefono_fijo?.length > 0 && data.area_telefono && data.area_telefono >= 0
			? `(60${data.area_telefono}) ${data.telefono_fijo}`
			: '';
	useEffect(() => {
		getConfig();
		getAdminUser();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getConfig = async () => {
		try {
			const res = (await requestAdministraCliente(
				`clientes-configuraciones-guias/${data.identificacion}`
			)) as any;
			if (res.isError) {
				throw new Error(res.message);
			}
			setValueHabilitar(res.data.estado_configuracion);
		} catch (error) {
			console.log(error);
		}
	};

	const getAdminUser = async () => {
		try {
			const res = (await requestBackendSuite(
				`administrador/cliente/${data.identificacion}`
			)) as any;
			if (res.isError) {
				throw new Error(res.message);
			}
			setAdminUser(res.data);
		} catch (error) {
			console.log(error);
			setAdminUser(null);
		}
	};
	const onChangeHabilitar = async (e: {
		target: { checked: boolean | ((prevState: boolean) => boolean) };
	}) => {
		try {
			const res = (await requestAdministraCliente(
				`clientes-configuraciones-guias/${data.identificacion}`,
				'PUT',
				{ estado: e.target.checked }
			)) as any;
			if (res.isError) {
				throw new Error(res.message);
			}
			getConfig();
		} catch (error) {
			console.log(error);
		}
	};
	return (
		<Grid
			container
			className={classes.infoContainer}
			id="customer-information"
			direction={isSmallScreen ? 'row' : 'column'}
		>
			<Grid item xs={12}>
				<Grid container alignItems="center" direction="column" justifyContent="center">
					<Grid item xs={12}>
						<Avatar
							id="customer-information-avatar"
							sx={{
								backgroundColor: stringToColor(data.nombre),
								height: 56,
								width: 56,
							}}
						>
							{getInitials(data.nombre)}
						</Avatar>
					</Grid>
					<Grid item style={{ marginTop: 12 }} xs={12}>
						<span className={classes.title}>Nombre o razón social:</span>
					</Grid>
					<Grid item xs={12}>
						<span className={classes.clientName} id="customer-information-name">
							{data.nombre}
						</span>
					</Grid>
					<Grid item style={{ marginTop: 16 }} xs={12}>
						<Grid container alignItems="center">
							<Grid item>
								<Typography
									color="rgba(0, 0, 0, 0.6)"
									fontFamily="Montserrat"
									fontSize={14}
								>
									Estado:
								</Typography>
							</Grid>
							<Grid item style={{ marginLeft: 8 }}>
								<Chip
									id="admin-customer-state"
									label={data.estado}
									size="small"
									style={{
										color: 'white',
										fontFamily: 'Montserrat',
										fontSize: 12,
										fontWeight: 600,
									}}
									sx={{
										bgcolor:
											data.estado === 'Activo'
												? 'success.main'
												: data.estado === 'Inactivo'
												? 'error.main'
												: '#686868',
									}}
								/>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Grid item style={{ marginTop: 24 }} xs={isSmallScreen ? 6 : 12}>
				{isSmallScreen && (
					<span className={classes.adminTitle} id="customer-information-title">
						Información Cliente
					</span>
				)}
				<ListItem
					id="customer-information-identification"
					style={{ maxWidth: 187 }}
					title="Identificación:"
					value={`${data.tipo_identificacion.nombre.toUpperCase()}: ${
						data.identificacion
					}`}
				/>
				<ListItem
					margin
					id="customer-information-phone-numbers"
					style={{ maxWidth: 187 }}
					title={`Teléfono${
						houseNumber.length > 0 && data.telefono_celular.length > 0 ? 's' : ''
					}:`}
					value={
						`${houseNumber}${
							houseNumber.length > 0 && data.telefono_celular.length > 0 ? ' - ' : ''
						}${data.telefono_celular}` || 'No registra'
					}
				/>
				<ListItem
					margin
					id="customer-information-address"
					style={{ maxWidth: 187 }}
					title="Dirección:"
					value={data.direccion}
				/>
			</Grid>
			<Grid item style={{ marginTop: isSmallScreen ? 24 : 16 }} xs={isSmallScreen ? 6 : 12}>
				<span className={classes.adminTitle} id="customer-information-admin">
					Información Administrador
				</span>
				<TooltipText
					id="customer-information-admin-name"
					style={{ maxWidth: 187 }}
					title="Nombre:"
					text={adminUser?.nombre || ''}
				/>
				<TooltipText
					margin
					id="customer-information-admin-email"
					style={{ maxWidth: 187 }}
					title="Correo:"
					text={adminUser?.correo || ''}
				/>
			</Grid>
			<Grid item md={12}>
				<div className={classes.containerConfig}>
					<span className={classes.adminTitle} id="customer-information-admin">
						Configuraciones
					</span>
					<Grid container alignItems="center" justifyContent="space-between">
						<Grid item>
							<Typography
								color="#707070"
								fontFamily="Montserrat"
								fontSize={14}
								fontWeight="500"
							>
								Habilitar guías
							</Typography>
						</Grid>
						<Grid item>
							<Switch
								checked={valueHabilitar}
								onChange={(e) => onChangeHabilitar(e)}
							/>
						</Grid>
					</Grid>
				</div>
			</Grid>
		</Grid>
	);
};

export default CustomerInformation;
